import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import Anleitung from '@/screens/Anleitung';
import { Aufgaben } from '@/screens/Aufgaben';
import { AufgabenExercise } from '@/screens/AufgabenExercise';
import { AufgabenExerciseText } from '@/screens/AufgabenExercise/text';
import { DigaCodes } from '@/screens/DigaCode';
import EpaScreen from '@/screens/EpaScreen';
import { Ergebnisse } from '@/screens/Ergebnisse';
import { Filler } from '@/screens/Filler';
import { Home } from '@/screens/HomePatient';
import { LicenseBooking } from '@/screens/LicenseBooking';
import { MarketingChannelQuestionnaire } from '@/screens/MarketingChannelQuestionnaire/';
import PasswordChangeScreen from '@/screens/PasswordChangeScreen';
import { PatientAccount } from '@/screens/PatientAccount';
import Results from '@/screens/Results';
import PatientEinwilligungen from '@/screens/PatientEinwilligungen';
import { Icon } from '@/components/Icon';
import colors, { appTheme } from '@/config/colors';
import { Patient } from '@/stores/store.types';
import { PatientDemoProvider } from '@/contexts/PatientDemoContext';
import { useMarketingChannelRedirect } from '@/hooks/useMarketingChannelRedirect';
import { DownloadProvider } from '@/contexts/DownloadAssetContext';

export type PatientStackParamList = {
  Anleitung: undefined;
  AufgabenExercise: undefined;
  AufgabenExerciseText: undefined;
  Aufgaben: {
    exerciseClass: string;
    patientId: string;
    setId: string;
    isNetworkError?: string;
    exerciseSettings: Record<string, unknown>;
    isSelf: boolean;
    selectedPatient?: Patient;
    activeSets?: unknown;
    preview: false;
  };
  DigaCodes: undefined;
  Home: undefined;
  Einwilligungen: undefined;
  EpaScreen: undefined;
  LicenseBooking: undefined;
  Login: undefined;
  MarketingChannelQuestionnaire: undefined;
  PasswordChangeScreen: undefined;
  PatientAccount: undefined;
  Privacy: undefined;
  ResultDetailScreen: undefined;
  ResultsScreen: undefined;
  Settings: undefined;
  SignUpController: undefined;
};

const Stack = createStackNavigator<PatientStackParamList>();

function PatientNavigationStack() {
  useMarketingChannelRedirect();

  return (
    <Stack.Navigator
      initialRouteName="Home"
      screenOptions={{
        animationEnabled: false,
        gestureEnabled: false,
        headerTintColor: colors.white,
        headerStyle: { backgroundColor: appTheme.primary },
        headerLeftContainerStyle: { paddingHorizontal: 8 },
        headerRightContainerStyle: { paddingHorizontal: 16 },
        headerBackTitle: 'Zurück',
        headerMode: 'float',
        headerTitleAlign: 'center',
        headerTitleStyle: {
          fontSize: 24,
        },
      }}
    >
      <Stack.Group
        screenOptions={{
          headerStyle: {
            height: 64,
            backgroundColor: appTheme.primary,
          },
          headerBackTitleStyle: {
            fontSize: 20,
          },
          headerTitleStyle: {
            fontSize: 24,
          },
          headerBackImage: (x) =>
            Platform.OS === 'ios' ? (
              <Icon name="chevronLeft" color={x.tintColor} size={38} />
            ) : (
              <Icon name="arrowLeft" color={x.tintColor} size={38} />
            ),
        }}
      >
        <Stack.Screen
          name="Privacy"
          component={Filler}
          options={{ headerShown: false, presentation: 'modal' }}
        />
        <Stack.Screen
          name="PatientAccount"
          component={PatientAccount}
          options={{
            headerTitle: 'Mein Konto',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Home"
          component={Home}
          options={{
            headerTitle: 'Übungsset auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen name="AufgabenExercise" component={AufgabenExercise} />
        <Stack.Screen
          name="AufgabenExerciseText"
          component={AufgabenExerciseText}
        />
        <Stack.Screen
          name="Aufgaben"
          component={Aufgaben}
          options={{
            title: 'Aufgaben auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Anleitung"
          component={Anleitung}
          options={{
            title: 'Anleitung',
            headerTitleAllowFontScaling: false,
          }}
        />

        <Stack.Screen
          name="Einwilligungen"
          component={PatientEinwilligungen}
          options={{
            title: 'Einwilligungen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="EpaScreen"
          component={EpaScreen}
          options={{
            title: 'Elektronische Patientenakte',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="MarketingChannelQuestionnaire"
          options={{
            title: '',
            headerTitleAllowFontScaling: false,
          }}
          component={MarketingChannelQuestionnaire}
        />
        <Stack.Screen
          name="PasswordChangeScreen"
          component={PasswordChangeScreen}
          options={{
            title: 'Passwort ändern',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultsScreen"
          component={Results}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultDetailScreen"
          component={Ergebnisse}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="DigaCodes"
          component={DigaCodes}
          options={{
            title: 'Rezeptcode einlösen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="LicenseBooking"
          component={LicenseBooking}
          options={{
            title: 'Lizenz buchen',
            headerTitleAllowFontScaling: false,
          }}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

export function PatientStack() {
  return (
    <DownloadProvider>
      <PatientDemoProvider>
        <PatientNavigationStack />
      </PatientDemoProvider>
    </DownloadProvider>
  );
}
